/**
 * Booking.com-style image categorisation (TASK-114). A curated, grouped subset
 * of Booking.com's Image Tag Type (ITT) taxonomy, each mapped to its ITT numeric
 * code so categories can later feed the channel-manager photo push (TASK-56).
 *
 * Source: Booking.com Connectivity "Image Tag Type (ITT)" codes.
 */

export type MediaCategoryGroup =
  | 'Room'
  | 'Property'
  | 'Views'
  | 'Facilities'
  | 'Food & drink'
  | 'Other';

export interface MediaCategory {
  /** Stable app key stored on property_media.category. */
  key: string;
  /** Human label shown in the console + storefront. */
  label: string;
  /** UI grouping. */
  group: MediaCategoryGroup;
  /** Booking.com Image Tag Type numeric code (for channel push). */
  ittCode: number;
}

export const MEDIA_CATEGORIES: MediaCategory[] = [
  // ── Room ───────────────────────────────────────────────────────────────────
  { key: 'whole_room', label: 'Photo of the whole room', group: 'Room', ittCode: 173 },
  { key: 'bedroom', label: 'Bedroom', group: 'Room', ittCode: 199 },
  { key: 'bed', label: 'Bed', group: 'Room', ittCode: 13 },
  { key: 'bathroom', label: 'Bathroom', group: 'Room', ittCode: 153 },
  { key: 'shower', label: 'Shower', group: 'Room', ittCode: 1 },
  { key: 'toilet', label: 'Toilet', group: 'Room', ittCode: 2 },
  { key: 'living_room', label: 'Living room', group: 'Room', ittCode: 159 },
  { key: 'kitchen', label: 'Kitchen or kitchenette', group: 'Room', ittCode: 158 },
  { key: 'dining_area', label: 'Dining area', group: 'Room', ittCode: 184 },
  { key: 'seating_area', label: 'Seating area', group: 'Room', ittCode: 182 },
  { key: 'balcony_terrace', label: 'Balcony / terrace', group: 'Room', ittCode: 157 },
  { key: 'view_from_room', label: 'View (from room)', group: 'Room', ittCode: 156 },

  // ── Property / exterior ──────────────────────────────────────────────────────
  { key: 'facade_entrance', label: 'Facade / entrance', group: 'Property', ittCode: 10 },
  { key: 'property_building', label: 'Property building', group: 'Property', ittCode: 3 },
  { key: 'lobby_reception', label: 'Lobby or reception', group: 'Property', ittCode: 160 },
  { key: 'garden', label: 'Garden', group: 'Property', ittCode: 103 },
  { key: 'patio', label: 'Patio', group: 'Property', ittCode: 4 },
  { key: 'swimming_pool', label: 'Swimming pool', group: 'Property', ittCode: 260 },
  { key: 'spa_wellness', label: 'Spa & wellness', group: 'Property', ittCode: 164 },
  { key: 'fitness_centre', label: 'Fitness centre / gym', group: 'Property', ittCode: 165 },
  { key: 'beach', label: 'Beach', group: 'Property', ittCode: 185 },
  { key: 'neighbourhood', label: 'Neighbourhood', group: 'Property', ittCode: 55 },

  // ── Views (sub-types) ────────────────────────────────────────────────────────
  { key: 'city_view', label: 'City view', group: 'Views', ittCode: 248 },
  { key: 'sea_view', label: 'Sea view', group: 'Views', ittCode: 255 },
  { key: 'mountain_view', label: 'Mountain view', group: 'Views', ittCode: 252 },
  { key: 'garden_view', label: 'Garden view', group: 'Views', ittCode: 249 },
  { key: 'pool_view', label: 'Pool view', group: 'Views', ittCode: 253 },
  { key: 'lake_view', label: 'Lake view', group: 'Views', ittCode: 250 },
  { key: 'river_view', label: 'River view', group: 'Views', ittCode: 254 },
  { key: 'landmark_view', label: 'Landmark view', group: 'Views', ittCode: 251 },
  { key: 'street_view', label: 'Street view', group: 'Views', ittCode: 256 },

  // ── Facilities ───────────────────────────────────────────────────────────────
  { key: 'restaurant', label: 'Restaurant / places to eat', group: 'Facilities', ittCode: 7 },
  { key: 'lounge_bar', label: 'Lounge or bar', group: 'Facilities', ittCode: 161 },
  { key: 'meeting_room', label: 'Meeting / conference room', group: 'Facilities', ittCode: 241 },
  { key: 'business_facilities', label: 'Business facilities', group: 'Facilities', ittCode: 177 },
  { key: 'kids_club', label: "Kids' club", group: 'Facilities', ittCode: 193 },
  { key: 'game_room', label: 'Game room', group: 'Facilities', ittCode: 102 },
  { key: 'on_site_shops', label: 'On-site shops', group: 'Facilities', ittCode: 125 },

  // ── Food & drink ─────────────────────────────────────────────────────────────
  { key: 'breakfast', label: 'Breakfast', group: 'Food & drink', ittCode: 264 },
  { key: 'food', label: 'Food', group: 'Food & drink', ittCode: 242 },
  { key: 'drinks', label: 'Drinks', group: 'Food & drink', ittCode: 273 },

  // ── Other ────────────────────────────────────────────────────────────────────
  { key: 'floor_plan', label: 'Floor plan', group: 'Other', ittCode: 183 },
  { key: 'decorative_detail', label: 'Decorative detail', group: 'Other', ittCode: 179 },
  { key: 'logo_sign', label: 'Property logo or sign', group: 'Other', ittCode: 50 },
  { key: 'other', label: 'Other', group: 'Other', ittCode: 172 },
];

/** Fast lookup set of valid category keys (for validation). */
export const MEDIA_CATEGORY_KEYS: string[] = MEDIA_CATEGORIES.map((c) => c.key);

export function isValidMediaCategory(key: string): boolean {
  return MEDIA_CATEGORY_KEYS.includes(key);
}
